/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import * as logger from '@ion/core/src/logger/logger';
import {removeAdditionalWhiteSpace} from '@ion/core/src/utils/StringUtil';
import {
  MonitorCategory,
  MonitorResults,
  ServiceTimings
} from '../types/types';

/**
 * @internal
 *
 * The `PerformanceMonitorLogger` provides a custom logging facility specific
 * to the `PerformanceMonitor` API.
 *
 * This API is primarily intended to be a package level instance, that is,
 * used internally by the underlying PerformanceMonitor instances.
 */
export default class PerformanceMonitorLogger {
  private _category: MonitorCategory;
  private _context: string;

  constructor(category: MonitorCategory) {
    this._category = category;
  }

  get context(): string {
    return this._context;
  }

  set context(context: string) {
    this._context = context;
  }

  static get isEnabled(): boolean {
    return logger.isEnabled();
  }

  static monitoringEnabled(enabled: boolean, enableEmission = true) {
    const message = `Application Performance Monitor ${enabled ? 'Enabled' : 'Disabled'}`;
    logger.info(!enableEmission ? `${message} [Logging Mode Only]` : message);
  }

  monitorStarted() {
    this.log(`Started Measuring ${this.getContextMessage()}`);
  }

  monitorStopped({
    totalTime,
    serviceTime,
    cumulativeServiceTime,
    totalUITime,
    duplications,
    measuredServices,
    serviceTimings
  }: MonitorResults) {
    const hasDuplicatedServices = duplications.length;
    const indent  = '\n\t  ';
    const results = `Completed Measuring: ${this._category || ''} ${this._context || ''}
    - Total Time                : ${getValue(totalTime)}
    - Total UI Time             : ${getValue(totalUITime)}
    - Service Time              : ${getValue(serviceTime)}
    - Service Time (Cumulative) : ${getValue(cumulativeServiceTime)}
    - Measured Services         : ${measuredServices?.length}
    - Duplicated Service Count  : ${hasDuplicatedServices}
    - Duplicated Services       : ${hasDuplicatedServices ? `${indent}${duplications?.toString().replace(',', indent)}` : 'None'}`;
    const message = `${results}\n    - Service Timings: \n${formatServiceTimings(serviceTimings)}`;
    this.log(message, 'info', false);
  }

  monitorStartWarning() {
    this.log(`Monitor ${this.getContextMessage()} has already been started, omitting start operation.`, 'warn');
  }

  monitorAutoStopWarning() {
    const message = `
      Monitor ${this.getContextMessage()} has automatically been stopped.
      This can occur when a page changes before the monitor has explicitly been stopped.
      The Monitor will flag the tracking of the page measurements to specify that the page was not explicitly
      stopped, which will allow for reviewers to ignore abnormally long timings as a false positive.`;
    this.log(message, 'warn');
  }

  monitorStopWarning() {
    this.log(`Monitor ${this.getContextMessage()} has already completed, omitting stop operation.`, 'warn');
  }

  monitorRestartWarning() {
    this.log(`Monitor restart invoked while monitor ${this.getContextMessage()} is currently running, omitting restart operation.`, 'warn');
  }

  monitorStopServicesWarning(services: string[]) {
    this.log(`Monitor has been stopped ${this.getContextMessage()} but currently has ${services.length} pending services: ${services.toString()}`, 'warn');
  }

  serviceStarted(service: string) {
    this.log(`Started Measuring Service: '${service}' ${this.getContextMessage()}`);
  }

  serviceStartWarning(service: string) {
    this.log(`Service '${service}' is already running ${this.getContextMessage()}, starting additional measurement.`, 'warn');
  }

  serviceBlacklistWarning(service: string, isStart = true) {
    this.log(`Service '${service}' has been blacklisted ${this.getContextMessage()}, omitting ${isStart ? 'start' : 'stop'} operation.`, 'warn');
  }

  serviceStopped(service: string, time: number) {
    this.log(`Completed Measuring Service: '${service}' ${this.getContextMessage()}. Service completed in: ${getValue(time)}`);
  }

  serviceStopWarning(service: string) {
    this.log(`Service '${service}' has already been stopped ${this.getContextMessage()}, omitting stop operation.`, 'warn');
  }

  monitorDisabledError(method: string) {
    this.log(`${method}(); invoked on disabled monitor, omitting operation.`, 'error');
  }

  monitorCrashEvent(page: string, message: string) {
    this.log(`Page ${page} Crash Event occurred. Error: ${message}`, 'error');
  }

  public static serviceRegisterDisabledError() {
    logger.error('Performance Monitor: ServiceMonitor invoked on disabled monitor, bypassing operation');
  }

  private log(message: string, type = 'info', normalizeSpacing = true) {
    const formatted = `${this._category} Performance Monitor: ${message}`;
    logger[type]?.(
      normalizeSpacing ? removeAdditionalWhiteSpace(formatted) : formatted
    );
  }

  private getContextMessage = (): string => (
    this._context ? `for '${this._context}'`: ''
  )
}

const getValue = (val: number): string => (
  `${(isNaN(val) || 0) ? 'N/A' : `${val}ms`}`
);

const formatServiceTimings = (timings: ServiceTimings): string => {
  if (timings) {
    let formatted = '';
    const items = Object.entries(timings) as any;
    for (const [url, measurements] of items) {
      formatted += `\t${url}:\n`;
      measurements.forEach((measurement, index) => {
        formatted += `\t\t${index+1}. duration: ${measurement.duration}\n`;
      });
    }
    return formatted;
  }
  return 'N/A';
};
